import { OrderListItem, OrderSearch } from "orderManagement/models";
import { PagedResults } from "common/models";
import apiClient from "./apiClient";

// Format a filter date as naive local midnight with NO timezone offset (e.g.
// "2026-06-02T00:00:00"). The legacy Dashboard casts its date params to a SQL `date`
// (no offset) and OMITS the bound entirely when the field is blank. We mirror both:
// emit the offset-free literal, and when the date is blank fall back to a sentinel that
// makes the bound effectively unbounded — matching Dashboard's blank start/end behavior.
const formatFilterDate = (d: { format: (f: string) => string } | null | undefined, unboundedFallback: string): string =>
  d ? d.format("YYYY-MM-DDTHH:mm:ss") : unboundedFallback;

const UNBOUNDED_START = "1900-01-01T00:00:00";
const UNBOUNDED_END = "9999-12-31T00:00:00";

// The search filters as the BFF's DispatchQueueSearchRequest takes them.
export const toSearchFilters = (options: OrderSearch) => ({
  clientIds: options.clientIds?.map(c=>c.id),
  orderStatusIds: options.orderStatusIds?.map(o=>o.id),
  tripTypes: options.tripTypes?.map(t=>t.value),
  isCancelled: options.isCancelled,
  isClosed: options.isClosed,
  isVerified: options.isVerified,
  isApproved: options.isApproved,
  isMpv: options.isMpv,
  isBargeClient: options.isBargeClient,
  isMapped: options.isMapped,
  isNearOtp: options.isNearOtp,
  startDate: formatFilterDate(options.startDate, UNBOUNDED_START),
  endDate: formatFilterDate(options.endDate, UNBOUNDED_END),
  hubIds: options.hubIds?.map(m=>m.id),
  driverHubIds: options.driverHubIds?.map(m=>m.id),
  closedByName: options.closedByName,
  isReviewQueueMode: options.isReviewQueueMode,
  driverResponse: options.driverResponse,
  litmusFilter: options.litmusFilter,
  eventResetFilter: options.eventResetFilter,
  isResettable: options.isResettable,
  alert: options.alert,
  dispatchQueueConfigId: options.dispatchQueueConfigId,
});

// The whole search request searchOrders sends: the filters plus sort and page. Bulk Event Reset's
// eligible-trips lookup sends the same object.
export const toSearchRequest = (options: OrderSearch) => ({
  ...toSearchFilters(options),
  sortBy: options.sortName,
  sortAsc: options.sortAsc,
  offset: options.pagedSearchOptions.offset,
  pageSize: options.pagedSearchOptions.pageSize,
});

const searchOrders = async (options: OrderSearch): Promise<PagedResults<OrderListItem>> => {
  return apiClient.post(`/order-management`, toSearchRequest(options));
};

const getAllTripIds = async (options: OrderSearch): Promise<number[]> => {
  return apiClient.post(`/order-management/ids`, toSearchFilters(options));
};

const getClosedByNames = async (): Promise<string[]> => {
  return apiClient.get(`/order-management/closed-by`);
};

const ordersService = {
  searchOrders,
  getAllTripIds,
  getClosedByNames,
};

export default ordersService;
